// Force-directed renderer — FXC-8538 (Warp).
//
// The first concrete `GraphRenderer`. Generalised from Bobbin's
// `DailyGraph` (FXC-8075/FXC-8109) ForceGraph2D block — same canvas
// drawing, link styling, and event wiring — so Bobbin's overlay UX is
// preserved bit-for-bit when it adopts the shared component. Canvas-
// based (~50KB gzipped), comfortable with the ~3000-node local spool
// graph in a small window; WebGL is overkill at this scale.

import { useEffect, useMemo, useRef } from "react";
import ForceGraph2D from "react-force-graph-2d";
import type { ForceGraphMethods } from "react-force-graph-2d";

import { colourFor, edgeColourFor } from "../core/palette";
import { edgeHitsHighlight } from "../core/search";
import type { GraphNode } from "../core/types";
import type { GraphControls, GraphRendererProps } from "./GraphRenderer";
import { drawGroupHull } from "./hull";
import { clusterForce } from "./clusterForce";

interface ForceNode extends GraphNode {
  _color: string;
  /** Cluster id (set when `groups` is provided) — drives the clustering force. */
  cluster?: string | number;
  // Layout-assigned coordinates (mutated by the force simulation).
  x?: number;
  y?: number;
  vx?: number;
  vy?: number;
}

interface ForceLink {
  source: string;
  target: string;
  predicate: string;
}

// react-force-graph types node ids as `string | number`; our canonical
// ids are strings. This is the shape the library hands back to event
// callbacks (it also bolts on x/y/vx/vy during layout).
type ForceCallbackNode = { id?: string | number };

/** Resolve a force-graph callback node back to its canonical id. */
function canonicalId(node: ForceCallbackNode | null): string | undefined {
  return node?.id == null ? undefined : String(node.id);
}

export function ForceRenderer({
  nodes,
  edges,
  width,
  height,
  highlightIds = null,
  searchActive = false,
  theme = "dark",
  colourForType = colourFor,
  colourForPredicate = edgeColourFor,
  nodeColor,
  groups = [],
  sizeByDegree = false,
  flowParticles = false,
  labelMode = "hover",
  onNodeClick,
  onNodeRightClick,
  onNodeHover,
  onReady,
}: GraphRendererProps) {
  const fgRef = useRef<ForceGraphMethods | undefined>(undefined);
  const onReadyRef = useRef(onReady);
  useEffect(() => {
    onReadyRef.current = onReady;
  }, [onReady]);

  // Hovered node id, held in a ref so a hover doesn't re-render the whole graph
  // (which would re-seed the layout). The canvas already repaints every frame,
  // so `nodeCanvasObject` reads the ref directly to draw the hover label.
  const hoveredIdRef = useRef<string | null>(null);

  // id → canonical node, so event callbacks hand back the canonical
  // model rather than the layout-mutated force node.
  const byId = useMemo(() => {
    const m = new Map<string, GraphNode>();
    for (const n of nodes) m.set(n.id, n);
    return m;
  }, [nodes]);

  // node id → degree (in + out), for optional degree-scaled node radius.
  const degreeOf = useMemo(() => {
    if (!sizeByDegree) return null;
    const m = new Map<string, number>();
    for (const e of edges) {
      m.set(e.source, (m.get(e.source) ?? 0) + 1);
      m.set(e.target, (m.get(e.target) ?? 0) + 1);
    }
    return m;
  }, [edges, sizeByDegree]);

  // node id → cluster id (from the hull groups), driving the clustering force.
  const clusterOf = useMemo(() => {
    const m = new Map<string, string | number>();
    for (const g of groups) for (const id of g.nodeIds) m.set(id, g.id);
    return m;
  }, [groups]);

  // ── Stability scaffolding (FXC-8538 zoom-jump fix) ───────────────────────
  //
  // react-force-graph mutates node objects in place (x/y/vx/vy from the
  // sim). Recreating those objects on every render discards the settled
  // layout and re-seeds it — so any re-render (hover, resize, parent state,
  // even a zoom-induced one) made the graph "reload" and fly apart. Three
  // things keep it put:
  //   1. Reuse the *same* ForceNode object for an unchanged id, mutating
  //      only its colour/cluster — so positions survive a recompute.
  //   2. Recompute `graphData` only when the data (id-set / edges) or the
  //      colour/cluster *mapping* actually changes — not just because a
  //      render produced a new `colourForType` / `nodeColor` function ref.
  //   3. Reheat the simulation only when the clustering genuinely changes,
  //      never on a bare reference change of `graphData`.

  // Current colour resolver, held in a ref so `graphData` can read it at
  // recompute time without listing the (possibly per-render) `nodeColor` /
  // `colourForType` identities in its dep array. The `colorSignature` below
  // (a value-stable string) is what gates recomputation, so referential
  // churn in those props never re-seeds the layout.
  const resolveColorRef = useRef<(n: GraphNode) => string>(() => "");
  resolveColorRef.current = (n: GraphNode): string =>
    nodeColor?.(n) ?? colourForType(n.type);

  // Signature of the colour mapping over the current nodes. Depends on the
  // real colour functions (so it reacts when colours genuinely change) but
  // emits a primitive string: when the resolved colours are identical the
  // string is value-equal, so `graphData` (keyed on this string) does NOT
  // recompute merely because a fresh `nodeColor` / `colourForType` reference
  // arrived from a re-render.
  const colorSignature = useMemo(
    () => nodes.map((n) => `${n.id} ${nodeColor?.(n) ?? colourForType(n.type)}`).join(""),
    [nodes, nodeColor, colourForType],
  );

  // Signature of the cluster assignment over the current nodes. Drives both
  // `graphData` (cluster value per node) and the reheat gate below.
  const clusterSignature = useMemo(
    () => nodes.map((n) => `${n.id} ${clusterOf.get(n.id) ?? ""}`).join(""),
    [nodes, clusterOf],
  );

  // Latest `clusterOf`, read inside the reheat effect without making that
  // effect depend on the Map's (per-render) identity. `groups` defaults to a
  // fresh `[]` each render, so `clusterOf` is a new Map every render; the
  // signature above is the stable gate.
  const clusterOfRef = useRef(clusterOf);
  clusterOfRef.current = clusterOf;

  // Cache of prior ForceNode objects by id, so an unchanged node keeps its
  // identity (and therefore its sim-assigned x/y/vx/vy) across recomputes.
  const nodeCacheRef = useRef<Map<string, ForceNode>>(new Map());

  // Build the force-graph data, REUSING existing node objects where the id
  // is unchanged (mutating colour/cluster in place) so the layout is
  // preserved. Only genuinely-new ids get fresh objects; removed ids drop.
  // Recomputes only when the node id-set/order, the edges, or the
  // colour/cluster mapping actually change — never just on a new render.
  const graphData = useMemo(() => {
    const cache = nodeCacheRef.current;
    const next = new Map<string, ForceNode>();
    const resolveColor = resolveColorRef.current;
    const cluster_ = clusterOfRef.current;
    const forceNodes = nodes.map<ForceNode>((n) => {
      const cluster = cluster_.get(n.id);
      const color = resolveColor(n);
      const existing = cache.get(n.id);
      if (existing) {
        // Mutate in place: keep x/y/vx/vy (and the object identity react-
        // force-graph already holds), refresh the derived fields. Also keep
        // the canonical fields current in case label/type/data changed for
        // the same id.
        existing.id = n.id;
        existing.type = n.type;
        existing.label = n.label;
        existing.data = n.data;
        existing._color = color;
        existing.cluster = cluster;
        next.set(n.id, existing);
        return existing;
      }
      const fresh: ForceNode = { ...n, _color: color, cluster };
      next.set(n.id, fresh);
      return fresh;
    });
    nodeCacheRef.current = next;
    return {
      nodes: forceNodes,
      links: edges.map<ForceLink>((e) => ({
        source: e.source,
        target: e.target,
        predicate: e.predicate,
      })),
    };
    // Colour/cluster resolvers are read through refs (current at recompute
    // time); `colorSignature`/`clusterSignature` change exactly when the
    // resolved colours / cluster assignments change, so they — not the raw,
    // per-render function/Map references — gate recomputation.
  }, [nodes, edges, colorSignature, clusterSignature]);

  // Install / remove the cluster-attraction force so clusters separate
  // spatially when grouping is active. Reheat ONLY when the clustering
  // actually changes — a bare re-render / zoom / hover must NOT reheat
  // (that was the perpetual-jump bug). `clusterSignature` is the stable
  // fingerprint of "which node is in which cluster".
  useEffect(() => {
    const fg = fgRef.current as
      | { d3Force?: (name: string, f: unknown) => void; d3ReheatSimulation?: () => void }
      | undefined;
    if (!fg || typeof fg.d3Force !== "function") return;
    const cluster = clusterOfRef.current;
    fg.d3Force("cluster", cluster.size > 0 ? clusterForce(0.14) : null);
    fg.d3ReheatSimulation?.();
    // Gate ONLY on the clustering fingerprint — not on `graphData` or
    // `clusterOf` identity. `clusterOf` is read through a ref (its latest
    // value); `clusterSignature` changes iff the assignment changes, so a
    // bare re-render / zoom / hover never reheats.
  }, [clusterSignature]);

  // Live id → force-node map for reading layout positions when drawing
  // cluster hulls (the objects are mutated in place by the simulation).
  const nodeObjById = useMemo(() => {
    const m = new Map<string, ForceNode>();
    for (const n of graphData.nodes) m.set(n.id, n);
    return m;
  }, [graphData]);

  // Draw cluster/clique hulls behind the nodes each frame.
  const renderFramePre = useMemo(() => {
    if (groups.length === 0) return undefined;
    return (ctx: CanvasRenderingContext2D, globalScale: number) => {
      for (const g of groups) {
        const pts: [number, number][] = [];
        for (const id of g.nodeIds) {
          const n = nodeObjById.get(id);
          if (n && n.x != null && n.y != null) pts.push([n.x, n.y]);
        }
        if (pts.length > 0) drawGroupHull(ctx, pts, g.color, globalScale);
      }
    };
  }, [groups, nodeObjById]);

  const readyControls = useMemo<GraphControls>(
    () => ({
      zoomToFit: (durationMs = 400, padding = 40) =>
        fgRef.current?.zoomToFit(durationMs, padding),
    }),
    [],
  );
  const hasOnReady = onReady !== undefined;

  // Expose imperative controls (zoom-to-fit) to the host.
  useEffect(() => {
    if (!hasOnReady) return;
    onReadyRef.current?.(readyControls);
    return () => onReadyRef.current?.(null);
  }, [hasOnReady, readyControls]);

  // Theme-aware label + highlight-ring colours. The canvas is transparent
  // (backgroundColor below), so the host surface shows through: on the light
  // V5 lineage surface the original white labels were invisible. Pick dark
  // text/ring for "light", light for "dark" (default).
  const isLight = theme === "light";
  const labelStrong = isLight ? "rgba(17,19,25,0.98)" : "rgba(255,255,255,0.95)";
  const labelMuted = isLight ? "rgba(28,31,40,0.82)" : "rgba(255,255,255,0.7)";
  const highlightRing = isLight ? "rgba(20,22,30,0.7)" : "rgba(255,255,255,0.9)";
  // Halo painted behind label text so it reads over edges + nodes (matches the
  // surface background — light on the V5 cream panel, dark on Bobbin).
  const labelHalo = isLight ? "rgba(247,247,244,0.92)" : "rgba(12,17,16,0.85)";

  // In hover mode, label the highlighted frontier (selected + 1-hop) ONLY when
  // it is small. Focusing a HUB (e.g. the X-57 volume mesh feeding ~2,837 runs)
  // makes every revealed node a 1-hop neighbour → the whole highlight set →
  // carpeting the canvas with hundreds of labels (the hairball we're killing).
  // Above the cap, rely on colour + shape at rest; hovering any node still
  // labels it individually.
  const HIGHLIGHT_LABEL_CAP = 12;
  const labelHighlightFrontier =
    labelMode === "all" || (highlightIds?.size ?? 0) <= HIGHLIGHT_LABEL_CAP;

  return (
    <ForceGraph2D
      ref={fgRef}
      width={width}
      height={height}
      graphData={graphData}
      nodeRelSize={3}
      linkDirectionalArrowLength={(l: ForceLink) =>
        l.predicate === "child_of" ? 0 : 3
      }
      linkDirectionalArrowRelPos={1}
      linkColor={(l: ForceLink) => colourForPredicate(l.predicate)}
      linkWidth={(l: ForceLink) =>
        searchActive && edgeHitsHighlight(l, highlightIds) ? 1.6 : 0.6
      }
      linkDirectionalParticles={(l: ForceLink) =>
        flowParticles && edgeHitsHighlight(l, highlightIds) ? 4 : 0
      }
      linkDirectionalParticleWidth={2}
      linkDirectionalParticleSpeed={0.006}
      backgroundColor="rgba(0,0,0,0)"
      cooldownTicks={120}
      onRenderFramePre={renderFramePre}
      onNodeClick={(node: ForceCallbackNode) => {
        const id = canonicalId(node);
        const canonical = id ? byId.get(id) : undefined;
        if (canonical) onNodeClick?.(canonical);
      }}
      onNodeRightClick={(node: ForceCallbackNode) => {
        const id = canonicalId(node);
        const canonical = id ? byId.get(id) : undefined;
        if (canonical) onNodeRightClick?.(canonical);
      }}
      onNodeHover={(node: ForceCallbackNode | null) => {
        const id = canonicalId(node);
        const canonical = id ? byId.get(id) : undefined;
        // Track the hovered id for hover-only labelling. The canvas repaints
        // every frame while the sim runs; once it cools, nudge a single repaint
        // so the hover label appears/clears without a fresh layout.
        if (hoveredIdRef.current !== (id ?? null)) {
          hoveredIdRef.current = id ?? null;
          const fg = fgRef.current as { d3ReheatSimulation?: () => void } | undefined;
          fg?.d3ReheatSimulation?.();
        }
        onNodeHover?.(canonical ?? null);
      }}
      nodeCanvasObjectMode={() => "after"}
      nodeCanvasObject={(rawNode: ForceCallbackNode, ctx: CanvasRenderingContext2D, scale: number) => {
        const node = rawNode as ForceNode;
        const label = String(node.label ?? "");
        const colour = node._color ?? colourForType(node.type ?? "");
        const isHighlight = highlightIds?.has(node.id) ?? false;
        const isHovered = hoveredIdRef.current === node.id;
        const base = degreeOf
          ? Math.min(9, 2.5 + Math.sqrt(degreeOf.get(node.id) ?? 0) * 1.3)
          : 3;
        const radius = isHighlight || isHovered ? base + 2 : base;
        const x = node.x ?? 0;
        const y = node.y ?? 0;
        const type = node.type ?? "";
        // Shape encodes the node class so type reads at a glance WITHOUT a label
        // (the dominant per-frame cost at scale) and survives colour-blindness +
        // zoom-out. Distinct cheap canvas shapes per provenance kind:
        //   operation/run/solve/case → rounded square (an activity)
        //   geometry                  → diamond
        //   *mesh*                    → triangle
        //   plot/saved_plot/dataframe → down-triangle
        //   everything else (scalars/values/artifacts) → circle
        const shape = shapeForType(type);
        // A bundle (collapsed fan-out of N leaf outputs) draws larger with an
        // outer ring so it reads as a stack/group, not a single value.
        const isBundle = type === "quantity_bundle";
        const r = isBundle ? Math.max(radius * 2.2, 7) : radius;
        ctx.beginPath();
        switch (shape) {
          case "square":
            if (typeof ctx.roundRect === "function") {
              ctx.roundRect(x - r, y - r, r * 2, r * 2, Math.min(r / 2.5, 3));
            } else {
              ctx.rect(x - r, y - r, r * 2, r * 2);
            }
            break;
          case "diamond":
            ctx.moveTo(x, y - r * 1.25);
            ctx.lineTo(x + r * 1.25, y);
            ctx.lineTo(x, y + r * 1.25);
            ctx.lineTo(x - r * 1.25, y);
            ctx.closePath();
            break;
          case "triangle":
            ctx.moveTo(x, y - r * 1.3);
            ctx.lineTo(x + r * 1.2, y + r);
            ctx.lineTo(x - r * 1.2, y + r);
            ctx.closePath();
            break;
          case "triangle-down":
            ctx.moveTo(x, y + r * 1.3);
            ctx.lineTo(x + r * 1.2, y - r);
            ctx.lineTo(x - r * 1.2, y - r);
            ctx.closePath();
            break;
          default:
            ctx.arc(x, y, r, 0, Math.PI * 2);
        }
        ctx.fillStyle = colour;
        ctx.fill();
        if (isBundle) {
          ctx.lineWidth = 1.5 / scale;
          ctx.strokeStyle = isLight ? "rgba(146,64,14,0.55)" : "rgba(255,255,255,0.55)";
          ctx.beginPath();
          ctx.arc(x, y, r + 2.5 / scale, 0, Math.PI * 2);
          ctx.stroke();
        }
        if (isHighlight || isHovered) {
          ctx.lineWidth = 1.2;
          ctx.strokeStyle = highlightRing;
          ctx.stroke();
        }
        // Labels are the dominant per-frame cost at scale (thousands of
        // `fillText`/`strokeText` calls freeze the main thread). Default
        // (`labelMode="hover"`): draw a label ONLY for the hovered node and the
        // highlighted set (selected + its 1-hop / search hits) — the node under
        // the cursor and the current focus frontier. `labelMode="all"` restores
        // the de-cluttered always-on labelling (structural spine + zoomed-in
        // non-leaves) for small graphs / surfaces that opt in.
        const isLeaf = type === "quantity_value";
        const showLabel =
          labelMode === "all"
            ? isHighlight || isHovered || (!isLeaf && scale > 0.55)
            : isHovered || (isHighlight && labelHighlightFrontier);
        if (showLabel) {
          ctx.font = `${11 / scale}px ui-sans-serif, system-ui, -apple-system, sans-serif`;
          ctx.textAlign = "left";
          ctx.textBaseline = "middle";
          const text = label.length > 30 ? `${label.slice(0, 30)}…` : label;
          const tx = x + r + 3 / scale;
          ctx.lineJoin = "round";
          ctx.lineWidth = 3 / scale;
          ctx.strokeStyle = labelHalo;
          ctx.strokeText(text, tx, y);
          ctx.fillStyle = isHighlight || isHovered ? labelStrong : labelMuted;
          ctx.fillText(text, tx, y);
        }
      }}
    />
  );
}

/** Node-canvas glyph for a node type — distinguishes kinds without a label. */
type NodeShape = "circle" | "square" | "diamond" | "triangle" | "triangle-down";

function shapeForType(type: string): NodeShape {
  // Activities (a computation/run) → square.
  if (type === "operation" || type === "run" || type === "solve" || type === "solver_run" || type === "case") {
    return "square";
  }
  // Geometry inputs → diamond.
  if (type === "geometry") return "diamond";
  // Meshes (surface / volume) → up-triangle.
  if (type.includes("mesh")) return "triangle";
  // Derived outputs people read off (plots / dataframes) → down-triangle.
  if (type === "plot" || type === "saved_plot" || type === "dataframe") return "triangle-down";
  // Scalars / values / misc artifacts → circle (the calm default).
  return "circle";
}
